import { useMemo } from "react";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import { getLanguage } from "src/utils/localization";
import { useProductFlags } from "src/hooks/recoil/useProductFlags";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface ProductOption {
  value: string;
  label: string;
  disabled?: boolean;
  visible?: boolean;
}

interface UseProductOptionsConfig {
  productKey: string;
  priceType?: string;
  isRenew?: boolean;
  currentPath?: string;
}

interface UseProductOptionsReturn {
  options: ProductOption[];
  currentValue: string;
  isSelectDisabled: boolean;
  showAsText: boolean;
}

/**
 * 제품별 구독 타입 옵션들을 관리하는 커스텀 훅
 *
 * @param config 옵션 설정 객체
 * @returns 제품 옵션 배열과 관련 상태들
 */
export const useProductOptions = ({
  productKey,
  priceType = "premium",
  isRenew = false,
  currentPath = "",
}: UseProductOptionsConfig): UseProductOptionsReturn => {
  const [searchParams] = useSearchParams();
  const { isOciProduct } = useProductFlags();
  const lang = getLanguage();
  const product = getProductFromSessionStorage();
  const { t } = useTranslation();
  // 현재 선택된 값 계산
  const currentValue = useMemo(() => {
    if (currentPath.includes("trial")) {
      return "trial";
    }
    if (priceType === "premium") {
      return "promotion";
    }
    return priceType;
  }, [currentPath, priceType]);

  // 셀렉트 비활성화 여부
  const isSelectDisabled = useMemo(() => {
    return isRenew || !isOciProduct;
  }, [isRenew, isOciProduct]);

  // 텍스트로만 표시할지 여부
  const showAsText = useMemo(() => {
    return (
      isOciProduct && !searchParams.has("pay") && !searchParams.has("type")
    );
  }, [isOciProduct, searchParams]);

  // 제품 옵션들 생성
  const options = useMemo(() => {
    const baseOptions: ProductOption[] = [];

    // 트라이얼 옵션 (조건부)
    if (product === "WRAPSODY_ECO" && lang === "en") {
      baseOptions.push({
        value: "trial",
        label: t(
          `siteType.subscriptionType.${productKey}.subscriptionType_free`
        ),
        visible: true,
      });
    }

    // 드라이브 옵션 (WRAPSODY 전용)
    if (product === "WRAPSODY") {
      baseOptions.push({
        value: "drive",
        label: t(
          `siteType.subscriptionType.${productKey}.subscriptionType_drive`
        ),
        visible: true,
      });
    }

    // 기본 옵션
    baseOptions.push({
      value: "basic",
      label: t(
        `siteType.subscriptionType.${productKey}.subscriptionType_basic`
      ),
      visible: true,
    });

    // 플러스 옵션
    baseOptions.push({
      value: "plus",
      label: t(`siteType.subscriptionType.${productKey}.subscriptionType_plus`),
      visible: true,
    });

    // 프리미엄 옵션 (조건부)
    if (!isOciProduct) {
      baseOptions.push({
        value: "promotion",
        label: t(`siteType.subscriptionType.wrapsody.subscriptionType_premium`),
        visible: true,
      });
    }

    // 보이는 옵션들만 필터링
    return baseOptions.filter((option) => option.visible);
  }, [productKey, product, lang, isOciProduct]);

  return {
    options,
    currentValue,
    isSelectDisabled,
    showAsText,
  };
};

/**
 * 제품별 타이틀과 라벨을 가져오는 훅
 */
export const useProductLabels = (priceType: string) => {
  const { t } = useTranslation();
  const title = useMemo(() => {
    if (!priceType) return "";
    return t(`siteType.${priceType}.orderSpecification.title`);
  }, [priceType]);

  const planTypeLabel = useMemo(() => {
    if (!priceType) return "";
    return t(`siteType.${priceType}.orderSpecification.planType`);
  }, [priceType]);

  return { title, planTypeLabel };
};

/**
 * OCI 제품에서 텍스트로 표시할 때 사용하는 훅
 */
export const useProductDisplayText = (productKey: string) => {
  const { t } = useTranslation();
  return useMemo(() => {
    return t(
      `siteType.subscriptionType.${productKey}.subscriptionType_premium`
    );
  }, [productKey]);
};
